/**
 * Gate dinámico de Clerk — perf + aislamiento de landings de video.
 *
 * Problema (PostHog replay 019f6c0a…, iPhone 375px): clerk.browser.js (~755ms)
 * + 4 chunks + /v1/environment + /v1/client cargaban en `/` compitiendo con el
 * teaser de Mux por el ancho de banda, cuando la landing solo necesita Clerk
 * AL CLICK (gate de registro / prefill de email).
 *
 * Problema (replays 019f7a5f / 019f7a75, IG IAB): en `/invitacion-premium` y
 * DualCTA, Clerk NO debe cargar en absoluto — el visitante solo ve el video.
 * Clerk en HTTP (secure-context:false) spameaba `digest` / production-key
 * errors y robaba red al stream Mux.
 *
 * Diseño:
 *  - `AppClerkProvider` (App.tsx) NO monta <ClerkProvider> mientras se está
 *    en un path diferido — Clerk NUNCA carga ahí; monta recién al navegar a
 *    un path no diferido (una vez montado no se desmonta). No se monta por
 *    interacción/idle: el flip a mitad de página remonta todo el subtree y
 *    reinicia el teaser Mux (code review 2026-07-16). Trade-off aceptado:
 *    un usuario ya logueado que toca el CTA free en `/` va a /registro,
 *    donde Clerk sí monta y su sesión activa lo redirige.
 *  - Componentes globales con hooks de Clerk (PostHogPageview, BannedGate,
 *    DevilThemeEffect) leen `useClerkGateMounted()` y renderizan su variante
 *    sin-Clerk mientras el provider no está — mismo patrón que CLERK_ENABLED.
 *  - Código imperativo (DualCTAVideoLanding al click) usa getClerkInstance()
 *    (window.Clerk, seteado por clerk-js al cargar) en vez de hooks.
 */
import { createContext, useContext } from "react";
import type { UserResource } from "@clerk/types";

/** true cuando <ClerkProvider> está montado y los hooks de Clerk son seguros. */
export const ClerkGateContext = createContext<boolean>(false);

export function useClerkGateMounted(): boolean {
  return useContext(ClerkGateContext);
}

/**
 * Paths públicos de video / DualCTA donde Clerk NO debe cargar.
 * Exact match tras normalizar trailing slash (excepto root).
 *
 * NO incluir: /admin/*, /registro, /portal/mi-acceso, /invitacion-premium/success
 * (flujos que sí necesitan sesión Clerk).
 */
const CLERK_DEFERRED_EXACT = new Set<string>([
  "/",
  "/video-landing",
  "/invitacion-premium",
  "/premium-invite",
  "/r/membresia-video",
]);

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

export function isClerkDeferredPath(pathname: string): boolean {
  return CLERK_DEFERRED_EXACT.has(normalizePathname(pathname));
}

type ClerkGlobal = {
  loaded?: boolean;
  user?: UserResource | null;
  session?: { getToken(): Promise<string | null> } | null;
};

/**
 * Acceso imperativo a clerk-js (window.Clerk). Devuelve null si Clerk todavía
 * no cargó — tratar como "signed out" (el default correcto para visitantes).
 */
export function getClerkInstance(): ClerkGlobal | null {
  if (typeof window === "undefined") return null;
  const clerk = (window as { Clerk?: ClerkGlobal }).Clerk;
  return clerk && clerk.loaded ? clerk : null;
}

export function getClerkUserImperative(): UserResource | null {
  return getClerkInstance()?.user ?? null;
}

export async function getClerkTokenImperative(): Promise<string | null> {
  try {
    return (await getClerkInstance()?.session?.getToken()) ?? null;
  } catch {
    return null;
  }
}
